import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Clock, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/Button';
export function Calendar() {
  const {
    t
  } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  // Mock data for calendar generation
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const plMonthNames = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];
  const timeSlots = ['09:00', '10:00', '11:30', '13:00', '14:30', '16:00', '17:30'];
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDate(null);
    setSelectedTime(null);
  };
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDate(null);
    setSelectedTime(null);
  };
  const isToday = (day: number) => {
    const today = new Date();
    return day === today.getDate() && currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
  };
  return <section id="calendar" className="py-24 bg-white" data-id="element-31">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-32">
        <div className="text-center mb-12" data-id="element-33">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-[#3D3D3D] mb-4" data-id="element-34">
            {t.calendar.title}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-35">
            {t.calendar.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto" data-id="element-36">
          {/* Calendar View */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-[#FAF7F5] rounded-3xl p-8 shadow-sm" data-id="element-37">
            <div className="flex justify-between items-center mb-8" data-id="element-38">
              <button onClick={handlePrevMonth} className="p-2 hover:bg-[#E8D5D5] rounded-full transition-colors" data-id="element-39">
                <ChevronLeft className="text-[#3D3D3D]" data-id="element-40" />
              </button>
              <h3 className="text-xl font-bold text-[#3D3D3D]" data-id="element-41">
                {t === t /* hack to check lang */ && (t.nav.about === 'O Nas' ? plMonthNames[currentDate.getMonth()] : monthNames[currentDate.getMonth()])}{' '}
                {currentDate.getFullYear()}
              </h3>
              <button onClick={handleNextMonth} className="p-2 hover:bg-[#E8D5D5] rounded-full transition-colors" data-id="element-42">
                <ChevronRight className="text-[#3D3D3D]" data-id="element-43" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-4 text-center" data-id="element-44">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <div key={day} className="text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-45">
                  {day}
                </div>)}
            </div>

            <div className="grid grid-cols-7 gap-2" data-id="element-46">
              {Array.from({
              length: firstDayOfMonth
            }).map((_, i) => <div key={`empty-${i}`} data-id="element-47" />)}
              {Array.from({
              length: daysInMonth
            }).map((_, i) => {
              const day = i + 1;
              const isSelected = selectedDate === day;
              const isCurrentDay = isToday(day);
              return <button key={day} onClick={() => {
                setSelectedDate(day);
                setSelectedTime(null);
              }} className={`
                      aspect-square rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200
                      ${isSelected ? 'bg-[#8B7E74] text-white shadow-md scale-105' : 'hover:bg-[#E8D5D5] text-[#3D3D3D]'}
                      ${isCurrentDay && !isSelected ? 'border-2 border-[#C9A96E]' : ''}
                    `} data-id="element-48">
                    {day}
                  </button>;
            })}
            </div>
          </motion.div>

          {/* Time Slots & Booking Info */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="flex flex-col justify-center" data-id="element-49">
            <AnimatePresence mode="wait" data-id="element-50">
              {selectedDate ? <motion.div key="slots" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-6" data-id="element-51">
                  <div className="flex items-center gap-3 text-[#8B7E74] mb-6" data-id="element-52">
                    <CalendarIcon size={24} data-id="element-53" />
                    <span className="text-xl font-medium" data-id="element-54">
                      {selectedDate}{' '}
                      {t.nav.about === 'O Nas' ? plMonthNames[currentDate.getMonth()] : monthNames[currentDate.getMonth()]}
                    </span>
                  </div>

                  <h4 className="text-lg font-semibold text-[#3D3D3D] mb-4" data-id="element-55">
                    {t.calendar.availableSlots}
                  </h4>

                  <div className="grid grid-cols-3 gap-3 mb-8" data-id="element-56">
                    {timeSlots.map(time => <button key={time} onClick={() => setSelectedTime(time)} className={`
                          py-2 px-4 rounded-lg text-sm font-medium border transition-all duration-200
                          ${selectedTime === time ? 'bg-[#C9A96E] text-white border-[#C9A96E] shadow-sm' : 'border-gray-200 text-gray-600 hover:border-[#C9A96E] hover:text-[#C9A96E]'}
                        `} data-id="element-57">
                        {time}
                      </button>)}
                  </div>

                  {selectedTime && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} data-id="element-58">
                      <div className="bg-[#FAF7F5] p-6 rounded-xl mb-6 border border-[#E8D5D5]" data-id="element-59">
                        <div className="flex items-center gap-2 text-[#3D3D3D] mb-2" data-id="element-60">
                          <Clock size={18} data-id="element-61" />
                          <span className="font-medium" data-id="element-62">
                            Selected: {selectedTime}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500" data-id="element-63">
                          Please confirm your appointment time.
                        </p>
                      </div>
                      <Button className="w-full" data-id="element-64">{t.calendar.bookBtn}</Button>
                    </motion.div>}
                </motion.div> : <motion.div key="empty" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="text-center py-12 px-6 bg-[#FAF7F5] rounded-3xl border-2 border-dashed border-[#E8D5D5]" data-id="element-65">
                  <CalendarIcon size={48} className="mx-auto text-[#D4A5A5] mb-4" data-id="element-66" />
                  <h3 className="text-xl font-medium text-[#3D3D3D] mb-2" data-id="element-67">
                    {t.calendar.selectDate}
                  </h3>
                  <p className="text-gray-500" data-id="element-68">{t.calendar.subtitle}</p>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>;
}